/**
 * StreetDiceThemes — three swappable visual "vibes" for the Street Dice room.
 * A random theme is picked per session (kept in sessionStorage), and the
 * player can hit the shuffle button in the room to force a fresh one.
 * Every theme exposes the same tokens so the room JSX doesn't need to branch.
 */

export const THEMES = {
    betohalics: {
        id: 'betohalics',
        name: 'Betohalics Gold',
        tagline: 'BLACK · GOLD · STREET-NIGHT',
        // Core palette — the official black/gold brand look
        accent: '#FFD700',
        accentSoft: '#FFC94D',
        glow: 'rgba(255,215,0,0.5)',
        // Full-page background — cinematic wet-alley photo under a dark wash
        pageBg: 'linear-gradient(180deg, rgba(0,0,0,0.80) 0%, rgba(0,0,0,0.86) 55%, rgba(0,0,0,0.92) 100%), url(/street-dice/alley-bg.jpg) center top / cover no-repeat, #050300',
        circleBg:
            'radial-gradient(circle at 50% 45%, #241d07 0%, #130f04 55%, #050300 100%)',
        circleBorder: '3px solid rgba(255,215,0,0.6)',
        headingFont: '"Bebas Neue", "Impact", sans-serif',
        headingLetter: '0.12em',
        dieBg: '#f5f2e8',
        diePip: '#111',
        dieBorder: '#FFD700',
        seatChip: 'rgba(255,215,0,0.12)',
        seatBorder: 'rgba(255,215,0,0.5)',
        potColor: '#FFD700',
        overlay: 'grain-street',
    },
    street: {
        id: 'street',
        name: 'Street Corner',
        tagline: 'ASPHALT · CHAIN-LINK · SPRAY PAINT',
        // Core palette
        accent: '#FF8A00',            // orange spray-paint
        accentSoft: '#FFAA33',
        glow: 'rgba(255,138,0,0.55)',
        // Full-page background
        pageBg: 'linear-gradient(180deg, #0d0d0d 0%, #141110 40%, #1a1512 100%)',
        // The "circle" the dice roll inside — asphalt
        circleBg:
            'radial-gradient(circle at 50% 50%, #2a2622 0%, #1a1614 55%, #0a0806 100%)',
        circleBorder: '2px dashed rgba(255,138,0,0.55)',
        // Header font stack — hand-painted stencil
        headingFont: '"Bebas Neue", "Impact", sans-serif',
        headingLetter: '0.14em',
        // Dice look — bone white with black pips
        dieBg: '#f5f2e8',
        diePip: '#111',
        dieBorder: '#c8c0a8',
        // Decorative flag / seat chip
        seatChip: 'rgba(255,138,0,0.15)',
        seatBorder: 'rgba(255,138,0,0.5)',
        potColor: '#FFAA33',
        // Ambient overlay pattern (grain + chain-link)
        overlay: 'grain-street',
    },
    speakeasy: {
        id: 'speakeasy',
        name: 'Speakeasy',
        tagline: 'DEEP GREEN FELT · BRASS LAMPS · WHISKEY',
        accent: '#D4A24C',
        accentSoft: '#EFC983',
        glow: 'rgba(212,162,76,0.55)',
        pageBg: 'linear-gradient(180deg, #0a1a14 0%, #0f2820 45%, #08170e 100%)',
        circleBg:
            'radial-gradient(circle at 50% 45%, #1e5439 0%, #123626 55%, #06180e 100%)',
        circleBorder: '2px solid rgba(212,162,76,0.55)',
        headingFont: '"Playfair Display", "Georgia", serif',
        headingLetter: '0.08em',
        dieBg: '#f5eede',
        diePip: '#2b1a08',
        dieBorder: '#8a6a3a',
        seatChip: 'rgba(212,162,76,0.14)',
        seatBorder: 'rgba(212,162,76,0.5)',
        potColor: '#EFC983',
        overlay: 'lamps-speakeasy',
    },
    neon: {
        id: 'neon',
        name: 'Neon Arcade',
        tagline: 'PURPLE · CYAN · RETRO PIXEL',
        accent: '#00E1FF',
        accentSoft: '#C77DFF',
        glow: 'rgba(0,225,255,0.55)',
        pageBg: 'linear-gradient(180deg, #0a021a 0%, #150733 45%, #050015 100%)',
        circleBg:
            'radial-gradient(circle at 50% 50%, #1a0a3a 0%, #0a0220 55%, #030014 100%)',
        circleBorder: '2px solid rgba(0,225,255,0.65)',
        headingFont: '"Press Start 2P", "VT323", monospace',
        headingLetter: '0.08em',
        dieBg: '#f5f0ff',
        diePip: '#4c0a72',
        dieBorder: '#00E1FF',
        seatChip: 'rgba(0,225,255,0.15)',
        seatBorder: 'rgba(0,225,255,0.5)',
        potColor: '#C77DFF',
        overlay: 'scanlines-neon',
    },
};

const THEME_KEY = 'street-dice-theme-v1';
const IDS = Object.keys(THEMES);

/** Load the current session theme. Defaults to the black/gold Betohalics
    brand look; players can still shuffle to the other themes for the session. */
export const getSessionTheme = () => {
    try {
        const stored = sessionStorage.getItem(THEME_KEY);
        if (stored && THEMES[stored]) return THEMES[stored];
    } catch { /* private mode / disabled — fall through */ }
    try { sessionStorage.setItem(THEME_KEY, 'betohalics'); } catch { /* ignore */ }
    return THEMES.betohalics;
};

/** Cycle to the next theme and persist it for the session. */
export const shuffleTheme = (currentId) => {
    const idx = IDS.indexOf(currentId);
    const next = IDS[(idx + 1) % IDS.length];
    try { sessionStorage.setItem(THEME_KEY, next); } catch { /* ignore */ }
    return THEMES[next];
};

/** Themed overlay pattern — rendered as an absolutely-positioned div. */
export const ThemeOverlay = ({ theme }) => {
    if (theme.overlay === 'grain-street') {
        return (
            <div
                className="pointer-events-none absolute inset-0 opacity-40 mix-blend-overlay"
                style={{
                    backgroundImage:
                        'repeating-linear-gradient(0deg, rgba(255,255,255,0.02) 0 1px, transparent 1px 3px),' +
                        'repeating-linear-gradient(90deg, rgba(255,255,255,0.02) 0 1px, transparent 1px 3px)',
                }}
            />
        );
    }
    if (theme.overlay === 'lamps-speakeasy') {
        return (
            <div
                className="pointer-events-none absolute inset-0 opacity-70"
                style={{
                    backgroundImage:
                        'radial-gradient(ellipse 60% 40% at 20% 0%, rgba(255,190,120,0.25) 0%, transparent 70%),' +
                        'radial-gradient(ellipse 60% 40% at 80% 100%, rgba(255,190,120,0.20) 0%, transparent 70%)',
                }}
            />
        );
    }
    if (theme.overlay === 'scanlines-neon') {
        return (
            <div
                className="pointer-events-none absolute inset-0 opacity-40 mix-blend-screen"
                style={{
                    backgroundImage:
                        'repeating-linear-gradient(0deg, rgba(0,225,255,0.06) 0 1px, transparent 1px 4px)',
                }}
            />
        );
    }
    return null;
};
